import { TypedUseLazyQueryStateResult, TypedUseQueryHookResult } from "@reduxjs/toolkit/query/react";
import { ComponentType, PropsWithChildren, memo } from "react";
import { Link } from "react-router-dom";

import { Button } from "@tsp/tsp-frontend-components/mui";

import { getExceptionFromUnknown } from "@tsp-core/exception/utils";
import { checkIsNotNullish } from "@tsp-core/utils/nullish";

import { AsyncStatus } from "../../enums";
import { calculateQueriesStatus } from "../../utils";

import {
  AsyncDataSuspenseBox,
  AsyncDataSuspenseCircularProgress,
  AsyncDataSuspenseException,
  AsyncDataSuspenseExceptions,
  AsyncDataSuspenseInner,
  AsyncDataSuspenseLink,
  AsyncDataSuspenseOverlay,
} from "./async-data-suspense.styles";

function DefaultLoader() {
  return (
    <AsyncDataSuspenseInner>
      <AsyncDataSuspenseOverlay>
        <AsyncDataSuspenseCircularProgress />
      </AsyncDataSuspenseOverlay>
    </AsyncDataSuspenseInner>
  );
}

function DefaultCatcher(props: { reason: unknown }) {
  let reason = props.reason;
  if (typeof reason === "object" && "error" in reason && checkIsNotNullish(reason.error)) {
    reason = reason.error;
  }

  return (
    <AsyncDataSuspenseExceptions>
      Something went wrong.
      <br />
      <AsyncDataSuspenseException>{`${getExceptionFromUnknown(reason)}`}</AsyncDataSuspenseException>
      <AsyncDataSuspenseLink>
        {/* @ts-expect-error braking types */}
        <Button component={Link} to="/">
          Go to main page
        </Button>
      </AsyncDataSuspenseLink>
    </AsyncDataSuspenseExceptions>
  );
}

export type AsyncDataSuspenseProps = PropsWithChildren<{
  queries: Array<TypedUseQueryHookResult<any, any, any> | TypedUseLazyQueryStateResult<any, any, any>>;
  Loader?: ComponentType;
  Catcher?: ComponentType<{ reason: unknown }>;
}>;

function AsyncDataSuspense(props: AsyncDataSuspenseProps) {
  const { queries, Loader = DefaultLoader, Catcher = DefaultCatcher, children } = props;

  const status = calculateQueriesStatus(queries);

  return (
    <AsyncDataSuspenseBox>
      {status === AsyncStatus.IDLE && <Loader />}
      {status === AsyncStatus.LOADING && <Loader />}
      {status === AsyncStatus.UPDATING && <Loader />}
      {status === AsyncStatus.FAILED && <Catcher reason={queries.find((query) => query.error)} />}
      {(status === AsyncStatus.UPDATING || status === AsyncStatus.COMPLETED) && children}
    </AsyncDataSuspenseBox>
  );
}

const Wrapper = memo(AsyncDataSuspense) as typeof AsyncDataSuspense;

export { Wrapper as AsyncDataSuspense };
